const {registerModule} = window;

const DRAG_THRESHOLD = 5;
// Default continuous leftward autoscroll speed, matched to the logo carousel
// (logoCloud). Overridable per entry via the `animationSpeed` field (data-speed).
const DEFAULT_SCROLL_SPEED = 30; // px per second
// Clamp frame delta so a backgrounded tab doesn't cause a large jump on resume.
const MAX_FRAME_MS = 50;
// Safety cap on how many clone sets we append while filling the viewport.
const MAX_CLONE_SETS = 20;
// Momentum (throw) after a drag release. Velocity is tracked in px/ms and decays
// by FRICTION each ms, so a flick coasts and eases back into the autoscroll.
const FRICTION = 0.9955;
const MIN_FLING_VELOCITY = 0.015; // px/ms (~15px/s): below this, resume autoscroll
const MAX_FLING_VELOCITY = 4; // px/ms: clamp so a fast flick can't launch wildly

/*
 * The track is driven by `transform: translateX(offset)` on the list — NOT by
 * native `scrollLeft`. Native scroll is hard-clamped to [0, maxScroll], so once
 * you reach the start you can't drag/scroll any further left. An offset is just
 * a number, so we can wrap it modulo the loop period and travel infinitely in
 * BOTH directions. Enough item sets are cloned to always fill the viewport plus
 * one period, which keeps the wrap seamless.
 */
class Timeline {
  constructor(element) {
    this.element = element;
    this.tick = this.tick.bind(this);
    this.initialize();
  }

  initialize() {
    this.bindUI();

    if (!this.ui.$track || !this.ui.$list || !this.ui.$list.children.length) {
      return;
    }

    this.applyOffset();
    this.bindEvents();
    this.bindAutoScrollEvents();

    // Measure in a rAF callback rather than inline: the callback runs just
    // before paint, and paint cannot happen until the CSSOM is ready, so the
    // list is guaranteed to be laid out as a grid. Measuring synchronously can
    // read a still-unstyled list, where every item shares an offsetLeft and the
    // period comes out as zero.
    requestAnimationFrame(() => {
      this.buildLoop();
      this.startAutoScroll();
    });
  }

  // Only the continuous marquee is gated. When it's off, the loop still runs
  // on demand for release momentum (see onPointerUp).
  startAutoScroll() {
    if (this.autoscrollEnabled && !this.reduceMotion) {
      this.startLoop();
    }
  }

  bindUI() {
    this.ui = {};
    this.ui.$track = this.element.querySelector('[data-ref="track"]');
    this.ui.$list = this.element.querySelector('[data-ref="list"]');

    // Autoscroll speed (px/s): CMS `animationSpeed` via data-speed, else default.
    const speed = parseFloat(this.element.dataset.speed);
    this.speed = Number.isFinite(speed) && speed > 0 ? speed : DEFAULT_SCROLL_SPEED;

    // CMS `autoscrollEnabled`: when false (data-autoscroll="false") the track
    // never autoscrolls, but drag + release momentum stay active.
    this.autoscrollEnabled = this.element.dataset.autoscroll !== 'false';

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Drag state.
    this.isPointerDown = false;
    this.didDrag = false;
    this.activePointerId = null;
    this.startX = 0;
    this.startOffset = 0;

    // Momentum state (px/ms), sampled from the last pointer moves before release.
    this.velocity = 0;
    this.lastMoveX = 0;
    this.lastMoveTime = 0;

    // Loop state. `offset` is the current translateX in px, always kept in the
    // half-open range (-period, 0].
    this.clonesBuilt = false;
    this.originals = [];
    this.period = 0;
    this.offset = 0;
    this.lastFrame = 0;
    this.isHovering = false;
    this.tabHidden = false;
    this.rafId = null;
    this.resizeObserver = null;
    this.resizeTimeout = null;
  }

  bindEvents() {
    this.ui.$track.addEventListener('pointerdown', this.onPointerDown.bind(this));
    this.ui.$track.addEventListener('pointermove', this.onPointerMove.bind(this));
    // Listen on window so a release is always caught, even when it happens off
    // the track (otherwise isPointerDown can stick and the drag "sticks").
    window.addEventListener('pointerup', this.onPointerUp.bind(this));
    window.addEventListener('pointercancel', this.onPointerUp.bind(this));
    // Capture phase so a drag can cancel the item link navigation before it fires.
    this.ui.$track.addEventListener('click', this.onClick.bind(this), true);
    this.ui.$track.addEventListener('dragstart', this.onDragStart.bind(this));
  }

  // -------------------------------------------------------------------------
  // Drag-to-scroll. `touch-action: pan-y` on the track lets vertical page
  // scrolling stay native while we own the horizontal axis for every pointer
  // type (mouse, pen, touch).
  // -------------------------------------------------------------------------

  onPointerDown(e) {
    // Ignore secondary mouse buttons; allow touch/pen (button is 0 for those).
    if (e.pointerType === 'mouse' && e.button !== 0) {
      return;
    }

    this.isPointerDown = true;
    this.didDrag = false;
    this.activePointerId = e.pointerId;
    this.startX = e.clientX;
    this.startOffset = this.offset;
    // Grabbing kills any in-flight momentum and starts fresh velocity sampling.
    this.velocity = 0;
    this.lastMoveX = e.clientX;
    this.lastMoveTime = performance.now();
  }

  onPointerMove(e) {
    if (!this.isPointerDown || e.pointerId !== this.activePointerId) {
      return;
    }

    // The button was released off-track before pointer capture kicked in: reset
    // so the drag can't stick.
    if (e.pointerType === 'mouse' && e.buttons === 0) {
      this.onPointerUp(e);
      return;
    }

    const delta = e.clientX - this.startX;

    // Without a period the offset cannot move, so entering drag mode would do
    // nothing except suppress the item's click in onClick.
    if (!this.didDrag && this.period && Math.abs(delta) > DRAG_THRESHOLD) {
      this.didDrag = true;
      this.ui.$track.classList.add('is-dragging');
      this.ui.$track.setPointerCapture(e.pointerId);
    }

    if (this.didDrag) {
      e.preventDefault();
      // Dragging right (positive delta) reveals earlier items; the modulo wrap
      // makes both directions endless.
      this.offset = this.normalize(this.startOffset + delta);
      this.applyOffset();

      // Sample velocity (lightly smoothed) for the release throw.
      const now = performance.now();
      const dt = now - this.lastMoveTime;
      if (dt > 0) {
        const sample = (e.clientX - this.lastMoveX) / dt;
        this.velocity = this.velocity * 0.2 + sample * 0.8;
        this.lastMoveX = e.clientX;
        this.lastMoveTime = now;
      }
    }
  }

  onPointerUp(e) {
    // Only react to the pointer that started the drag, so an unrelated pointer
    // release elsewhere on the page can't end an in-progress drag.
    if (!this.isPointerDown || e.pointerId !== this.activePointerId) {
      return;
    }

    this.isPointerDown = false;
    this.activePointerId = null;
    this.ui.$track.classList.remove('is-dragging');

    // Clamp so a very fast flick can't launch the throw wildly. The tick loop
    // picks up this velocity and coasts (a click without a drag leaves it 0).
    this.velocity = Math.max(-MAX_FLING_VELOCITY, Math.min(MAX_FLING_VELOCITY, this.velocity));

    // Kick the loop for the throw. Needed when autoscroll is off (the loop is
    // otherwise idle); a no-op when it's already running. Reduced motion opts
    // out of momentum entirely.
    if (!this.reduceMotion && Math.abs(this.velocity) > MIN_FLING_VELOCITY) {
      this.startLoop();
    }
  }

  onClick(e) {
    // Suppress only the mouse/touch click synthesized by a drag. Keyboard-
    // activated clicks report detail === 0, so they always navigate even if
    // didDrag is stale from a drag that never produced a click.
    if (this.didDrag && e.detail > 0) {
      e.preventDefault();
      e.stopPropagation();
    }

    this.didDrag = false;
  }

  onDragStart(e) {
    e.preventDefault();
  }

  // -------------------------------------------------------------------------
  // Infinite leftward autoscroll.
  // -------------------------------------------------------------------------

  bindAutoScrollEvents() {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (query.addEventListener) {
      query.addEventListener('change', (e) => {
        this.reduceMotion = e.matches;
        if (this.reduceMotion) {
          this.stopLoop();
        } else {
          this.startAutoScroll();
        }
      });
    }

    // Pause on mouse hover only (not touch, not keyboard focus).
    this.ui.$track.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse') {
        this.isHovering = true;
      }
    });
    this.ui.$track.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'mouse') {
        this.isHovering = false;
      }
    });

    document.addEventListener('visibilitychange', () => {
      this.tabHidden = document.hidden;
    });

    // A page restored from the back/forward cache resumes carrying whatever rAF
    // handle was outstanding when it was frozen, so re-arm instead of trusting
    // it. startLoop cancels the old handle, so this is still correct if the
    // browser did preserve the queued callback.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        this.startAutoScroll();
      }
    });

    // Watching the track covers both cases that invalidate the period: item
    // widths change across breakpoints, and a stylesheet that lands after the
    // initial measurement resizes the track. Component CSS is not
    // render-blocking outside development, so the late case is routine.
    this.resizeObserver = new ResizeObserver(this.onResize.bind(this));
    this.resizeObserver.observe(this.ui.$track);
  }

  // Start the rAF loop (used for both the autoscroll marquee and drag momentum).
  // Cancelling first, rather than bailing on a non-null rafId, keeps this safe
  // to call at any time: a frame that was requested but never delivered cannot
  // lock the loop out permanently.
  startLoop() {
    if (!this.period) {
      return;
    }

    this.stopLoop();
    this.lastFrame = performance.now();
    this.rafId = requestAnimationFrame(this.tick);
  }

  stopLoop() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  // Duplicate the item set enough times to fill the viewport plus one full set,
  // so wrapping by one period is always seamless. Clones are hidden from
  // assistive tech and removed from the tab order. Safe to call again after a
  // measurement that produced no period: the originals and the first clone set
  // are each only captured once, so a retry re-measures rather than mistaking
  // the previous attempt's clones for originals.
  buildLoop() {
    if (!this.originals.length) {
      this.originals = Array.from(this.ui.$list.children);
    }

    if (!this.originals.length) {
      return;
    }

    if (!this.ui.$list.querySelector('[data-clone="true"]')) {
      this.appendCloneSet();
    }

    const firstClone = this.ui.$list.querySelector('[data-clone="true"]');
    this.period = firstClone.offsetLeft - this.originals[0].offsetLeft;

    if (!this.period) {
      return;
    }

    this.fillViewport();
    this.clonesBuilt = true;
  }

  appendCloneSet() {
    this.originals.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.clone = 'true';
      clone.querySelectorAll('a').forEach((link) => {
        link.setAttribute('tabindex', '-1');
      });
      this.ui.$list.appendChild(clone);
    });
  }

  fillViewport() {
    let sets = 1;
    while (
      this.ui.$list.scrollWidth < this.period + this.ui.$track.clientWidth &&
      sets < MAX_CLONE_SETS
    ) {
      this.appendCloneSet();
      sets += 1;
    }
  }

  onResize() {
    window.clearTimeout(this.resizeTimeout);
    this.resizeTimeout = window.setTimeout(() => {
      this.remeasureLoop();
    }, 150);
  }

  remeasureLoop() {
    if (!this.originals.length) {
      return;
    }

    // The loop was never built because the first measurement found no period.
    // Retry now that something has resized, instead of staying dead for the
    // life of the page.
    if (!this.clonesBuilt) {
      this.buildLoop();
      this.startAutoScroll();
      return;
    }

    const firstClone = this.ui.$list.querySelector('[data-clone="true"]');

    if (!firstClone) {
      return;
    }

    // Item widths change across breakpoints, so recompute the loop period and
    // top up clones if the viewport grew, then re-wrap the current offset.
    this.period = firstClone.offsetLeft - this.originals[0].offsetLeft;
    this.fillViewport();
    this.offset = this.normalize(this.offset);
    this.applyOffset();
  }

  // Wrap an offset into the half-open range (-period, 0]. Because every set is
  // identical, snapping by a whole period is visually seamless.
  normalize(value) {
    if (!this.period) {
      return 0;
    }

    let wrapped = value % this.period;
    if (wrapped > 0) {
      wrapped -= this.period;
    }

    return wrapped;
  }

  applyOffset() {
    this.ui.$list.style.transform = `translate3d(${this.offset}px, 0, 0)`;
  }

  isPaused() {
    return this.isHovering || this.isPointerDown || this.tabHidden;
  }

  tick(now) {
    const dt = Math.min(now - this.lastFrame, MAX_FRAME_MS);
    this.lastFrame = now;

    if (this.isPointerDown) {
      // The drag handler owns the offset while the pointer is down.
    } else if (Math.abs(this.velocity) > MIN_FLING_VELOCITY) {
      // Coast on the release throw, decaying by friction until it settles.
      this.offset = this.normalize(this.offset + this.velocity * dt);
      this.velocity *= FRICTION ** dt;
      this.applyOffset();
    } else if (this.autoscrollEnabled && !this.isPaused()) {
      this.velocity = 0;
      this.offset = this.normalize(this.offset - (this.speed * dt) / 1000);
      this.applyOffset();
    } else if (!this.autoscrollEnabled) {
      // Momentum has settled and there's no marquee to run: idle, so stop the
      // loop until the next drag release restarts it.
      this.velocity = 0;
      this.stopLoop();
      return;
    }

    this.rafId = requestAnimationFrame(this.tick);
  }
}

registerModule('Timeline', Timeline);
